import React from 'react';

import { Badge } from 'primereact/badge';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';

import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { CommentItem } from './CommentItem';
import { useTaskComments } from './hooks/useTaskComments';

interface TaskCommentsComponentProps {
  taskId: number;
  setHasNewComments: (value: boolean) => void;
  setHasComments:  (value: boolean) => void;
}

export const TaskCommentsComponent: React.FC<TaskCommentsComponentProps> = ({ taskId, setHasNewComments, setHasComments }) => {
  const {
    toast,
    fileInputRef,
    editFileInputRef,
    comments,
    commentText,
    setCommentText,
    error,
    setError,
    loading,
    sending,
    pendingFiles,
    deletingCommentId,
    editingCommentId,
    editCommentText,
    setEditCommentText,
    editPendingFiles,
    setEditPendingFiles,
    editExistingAttachments,
    setEditExistingAttachments,
    setDeletedAttachmentIds,
    savingEdit,
    getCommentsByTask,
    handleFileSelect,
    handleRemoveFile,
    handleSendComment,
    handleEditComment,
    handleCancelEdit,
    handleEditFileSelect,
    handleSaveEditComment,
    handleDeleteComment,
    handleAttachmentDownload,
    handleAttachmentDoubleClick,
    getAttachmentPreview,
    getDeviceIcon,
    formatDate,
    MAX_FILES,
    MAX_SIZE_MB,
  } = useTaskComments({ taskId, setHasNewComments, setHasComments });

  return (
    <div className="p-4 task-comments">
      <Toast ref={toast} position="top-right" />
      <Tooltip target="[data-pr-tooltip]" mouseTrack mouseTrackLeft={10} />
      
      <div className="mb-4">
        <div className="relative">
          <InputTextarea
            value={commentText}
            onChange={(e) => {
              if (e.target.value.length <= 400) {
                setCommentText(e.target.value);
                if (e.target.value.trim().length > 0) {
                  setError('');
                }
              }
            }}
            placeholder="Введите ваш комментарий..."
            rows={4}
            maxLength={400}
            className="w-full"
            style={{ fontSize: '14px' }}
            disabled={sending}
          />
          
          <Badge 
            value={`${commentText.length}/400`} 
            severity={commentText.length >= 380 ? 'warning' : undefined}
            className="absolute"
            style={{ 
              bottom: '8px', 
              right: '8px',
              zIndex: 1
            }}
          />
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <div className="flex justify-content-between align-items-center mt-2">
          <div className="flex align-items-center gap-2 flex-wrap">
            {pendingFiles.map((pendingFile) => (
              <div key={pendingFile.id} className="flex align-items-center gap-2 bg-gray-100 p-2 border-round" style={{ borderRadius: '8px' }}>
                {pendingFile.preview ? (
                  <img src={pendingFile.preview} alt="preview" style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px' }} />
                ) : (
                  <i className="pi pi-file" style={{ fontSize: '24px', color: '#6c757d' }} />
                )}
                <span className="text-sm" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {pendingFile.file.name}
                </span>
                <span className="text-xs text-500">{(pendingFile.file.size / 1024 / 1024).toFixed(2)} MB</span>
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-rounded p-button-danger"
                  onClick={() => handleRemoveFile(pendingFile.id)}
                  style={{ width: '24px', height: '24px' }}
                  disabled={sending}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              icon="pi pi-paperclip"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || pendingFiles.length >= MAX_FILES}
              outlined
              tooltip="Добавить вложения"
              tooltipOptions={{ position: 'top' }}
            />
            
            <Button
              label="Отправить"
              icon="pi pi-send"
              onClick={handleSendComment}
              disabled={!commentText.trim() || sending || pendingFiles.length > MAX_FILES}
              severity={!commentText.trim() || pendingFiles.length > MAX_FILES ? 'secondary' : undefined}
              raised
              loading={sending}
            />
          </div>
        </div>
      </div>

      <Divider type="solid" />

      <div>
        {loading ? (
          <div className="flex flex-column align-items-center justify-content-center p-4">
            <i className="pi pi-spin pi-spinner text-4xl text-400 mb-2" />
            <p className="text-500 m-0 text-center">Загрузка комментариев...</p>
          </div>
        ) : comments.length === 0 ? (
          <>
            <div className="flex flex-column align-items-center justify-content-center p-4">
              <i className="pi pi-inbox text-4xl text-400 mb-2" />
              <p className="text-500 m-0 text-center">Примечаний нет.</p>
            </div>
            <div className="flex justify-content-center mt-3">
              <Button
                label="Обновить"
                icon="pi pi-refresh"
                onClick={() => getCommentsByTask()}
                className="p-button-outlined"
              />
            </div>
          </>
        ) : (
          <>
            <ScrollPanel style={{ width: '100%', height: '400px' }}>
              {comments.map((comment) => {
                const isEditing = editingCommentId === comment.id;
                
                return (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    isEditing={isEditing}
                    editCommentText={editCommentText}
                    setEditCommentText={setEditCommentText}
                    editPendingFiles={editPendingFiles}
                    editExistingAttachments={editExistingAttachments}
                    setDeletedAttachmentIds={setDeletedAttachmentIds}
                    setEditPendingFiles={setEditPendingFiles}
                    setEditExistingAttachments={setEditExistingAttachments}
                    savingEdit={savingEdit}
                    deletingCommentId={deletingCommentId}
                    editingCommentId={editingCommentId}
                    getDeviceIcon={getDeviceIcon}
                    getAttachmentPreview={getAttachmentPreview}
                    formatDate={formatDate}
                    handleEditComment={handleEditComment}
                    handleDeleteComment={handleDeleteComment}
                    handleCancelEdit={handleCancelEdit}
                    handleSaveEditComment={handleSaveEditComment}
                    handleEditFileSelect={handleEditFileSelect}
                    handleAttachmentDoubleClick={handleAttachmentDoubleClick}
                    handleAttachmentDownload={handleAttachmentDownload}
                    editFileInputRef={editFileInputRef}
                    MAX_FILES={MAX_FILES}
                  />
                );
              })}
            </ScrollPanel>
            <div className="flex justify-content-center mt-3">
              <Button
                label="Обновить"
                icon="pi pi-refresh"
                onClick={() => getCommentsByTask()}
                className="p-button-outlined"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};